function normalizeText(value) {
  return value === null || value === undefined ? "" : String(value).trim();
}

function buildApiUrl(hostname, path) {
  const base = normalizeText(hostname || window.hostname || "");
  if (!base) {
    return path;
  }

  const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

function isObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export function normalizeRequiredCustomIdentifierKey(value) {
  return normalizeText(value);
}

export function normalizeRequiredCustomIdentifiers(rawIdentifiers) {
  const identifiers = Array.isArray(rawIdentifiers) ? rawIdentifiers : [];
  const normalizedKeys = identifiers
    .map((identifier) => {
      if (typeof identifier === "string" || typeof identifier === "number") {
        return normalizeRequiredCustomIdentifierKey(identifier);
      }

      if (isObject(identifier)) {
        if (identifier.value !== undefined && identifier.value !== null) {
          return normalizeRequiredCustomIdentifierKey(identifier.value);
        }

        if (identifier.key !== undefined && identifier.key !== null) {
          return normalizeRequiredCustomIdentifierKey(identifier.key);
        }

        if (identifier.identifier_key !== undefined && identifier.identifier_key !== null) {
          return normalizeRequiredCustomIdentifierKey(identifier.identifier_key);
        }
      }

      return "";
    })
    .filter((key) => key.length > 0);

  return [...new Set(normalizedKeys)];
}

export function parseRequiredCustomIdentifiers(rawIdentifiers) {
  let parsedIdentifiers = rawIdentifiers;

  if (typeof parsedIdentifiers === "string") {
    try {
      parsedIdentifiers = JSON.parse(parsedIdentifiers);
    } catch (error) {
      parsedIdentifiers = [];
    }
  }

  return normalizeRequiredCustomIdentifiers(parsedIdentifiers);
}

export function buildCustomIdentifierKeyOptions(keys) {
  return (Array.isArray(keys) ? keys : [])
    .filter((keyDefinition) => keyDefinition && keyDefinition.is_active !== false)
    .map((keyDefinition) => {
      const key = normalizeText(keyDefinition.key);
      const text = normalizeText(keyDefinition.display_name) || key;

      return {
        value: key,
        text,
        metadata: {
          ...keyDefinition,
          key,
          display_name: text,
        },
      };
    })
    .filter((option) => option.value !== "");
}

export function buildSelectedCustomIdentifierKeys(keys) {
  return normalizeRequiredCustomIdentifiers(keys).map((key) => ({
    value: key,
    text: key,
  }));
}

export function mergeCustomIdentifierKeyOptions(options, selectedKeys) {
  const mergedOptions = Array.isArray(options) ? [...options] : [];
  const knownKeys = mergedOptions.map((option) => normalizeRequiredCustomIdentifierKey(option && option.value));

  normalizeRequiredCustomIdentifiers(selectedKeys).forEach((key) => {
    if (!knownKeys.includes(key)) {
      mergedOptions.push({ value: key, text: key, metadata: { key, display_name: key } });
    }
  });

  return mergedOptions;
}

export function extractApiErrorMessage(error, fallbackMessage = "Something went wrong. Please try again.") {
  const responseData = error && error.response ? error.response.data : null;

  if (responseData) {
    if (typeof responseData.message === "string" && responseData.message.trim().length > 0) {
      return responseData.message.trim();
    }

    if (isObject(responseData.errors)) {
      const messages = Object.values(responseData.errors)
        .flat()
        .map((message) => normalizeText(message))
        .filter((message) => message.length > 0);

      if (messages.length > 0) {
        return messages.join(" ");
      }
    }
  }

  return fallbackMessage;
}

export function extractFieldErrors(error, fieldName) {
  const responseData = error && error.response ? error.response.data : null;
  if (!responseData || !isObject(responseData.errors)) {
    return [];
  }

  const fieldErrors = responseData.errors[fieldName];
  if (!Array.isArray(fieldErrors)) {
    return [];
  }

  return fieldErrors.map((message) => normalizeText(message)).filter((message) => message.length > 0);
}

export async function fetchCustomIdentifierKeys(axios, hostname, propertyId) {
  const normalizedPropertyId = normalizeText(propertyId);
  const config = normalizedPropertyId
    ? { params: { property_id: normalizedPropertyId } }
    : {};

  const response = await axios.get(
    buildApiUrl(hostname, "/api/auth/custom-identifier/keys"),
    config
  );

  return response && response.data && Array.isArray(response.data.data) ? response.data.data : [];
}

export async function createCustomIdentifierKey(axios, hostname, payload) {
  return axios.post(buildApiUrl(hostname, "/api/auth/custom-identifier/keys"), payload);
}

export async function updateCustomIdentifierKey(axios, hostname, keyId, payload) {
  return axios.patch(buildApiUrl(hostname, `/api/auth/custom-identifier/keys/${keyId}`), payload);
}

export async function fetchCustomIdentifierKeyValues(axios, hostname, keyId) {
  const response = await axios.get(buildApiUrl(hostname, `/api/auth/custom-identifier/keys/${keyId}/values`));
  return response && response.data && Array.isArray(response.data.data) ? response.data.data : [];
}

export async function createCustomIdentifierKeyValue(axios, hostname, keyId, payload) {
  return axios.post(buildApiUrl(hostname, `/api/auth/custom-identifier/keys/${keyId}/values`), payload);
}

export async function updateCustomIdentifierKeyValue(axios, hostname, keyId, valueId, payload) {
  return axios.patch(buildApiUrl(hostname, `/api/auth/custom-identifier/keys/${keyId}/values/${valueId}`), payload);
}

export async function fetchCustomIdentifierRelationships(axios, hostname, propertyId) {
  const normalizedPropertyId = normalizeText(propertyId);
  const config = normalizedPropertyId
    ? { params: { property_id: normalizedPropertyId } }
    : {};

  const response = await axios.get(
    buildApiUrl(hostname, "/api/auth/custom-identifier/relationships"),
    config
  );

  return response && response.data && Array.isArray(response.data.data) ? response.data.data : [];
}

export async function createCustomIdentifierRelationship(axios, hostname, payload) {
  return axios.post(buildApiUrl(hostname, "/api/auth/custom-identifier/relationships"), payload);
}

export async function updateCustomIdentifierRelationship(axios, hostname, relationshipId, payload) {
  return axios.patch(buildApiUrl(hostname, `/api/auth/custom-identifier/relationships/${relationshipId}`), payload);
}

export async function deleteCustomIdentifierRelationship(axios, hostname, relationshipId) {
  return axios.delete(buildApiUrl(hostname, `/api/auth/custom-identifier/relationships/${relationshipId}`));
}

export async function fetchCustomIdentifierRelationshipValues(axios, hostname, relationshipId) {
  const response = await axios.get(buildApiUrl(hostname, `/api/auth/custom-identifier/relationships/${relationshipId}/values`));
  return response && response.data && Array.isArray(response.data.data) ? response.data.data : [];
}

export async function createCustomIdentifierRelationshipValue(axios, hostname, relationshipId, payload) {
  return axios.post(buildApiUrl(hostname, `/api/auth/custom-identifier/relationships/${relationshipId}/values`), payload);
}

export async function updateCustomIdentifierRelationshipValue(axios, hostname, relationshipId, valueId, payload) {
  return axios.patch(buildApiUrl(hostname, `/api/auth/custom-identifier/relationships/${relationshipId}/values/${valueId}`), payload);
}

export async function deleteCustomIdentifierRelationshipValue(axios, hostname, relationshipId, valueId) {
  return axios.delete(buildApiUrl(hostname, `/api/auth/custom-identifier/relationships/${relationshipId}/values/${valueId}`));
}

export async function fetchPermitCustomIdentifierOptions(axios, hostname, propertyId) {
  const normalizedPropertyId = normalizeText(propertyId);
  const payload = normalizedPropertyId ? { property_id: normalizedPropertyId } : {};
  const response = await axios.post(buildApiUrl(hostname, "/api/auth/permit/custom-identifier-options"), payload);

  return response && response.data && response.data.status ? response.data.data || {} : {};
}

export async function fetchPermitTypesForProperty(axios, hostname, propertyId) {
  const normalizedPropertyId = normalizeText(propertyId);
  const payload = {
    commercial: 1,
    page: 1,
    limit: 500,
    all: 1,
  };
  if (normalizedPropertyId) {
    payload.property_id = normalizedPropertyId;
  }

  const response = await axios.post(buildApiUrl(hostname, "/api/auth/permit/list"), {
    ...payload,
  });

  if (!(response && response.data && response.data.status && response.data.data)) {
    return [];
  }

  return Array.isArray(response.data.data.permit_list) ? response.data.data.permit_list : [];
}

export async function fetchCustomerIdentifierAudit(axios, hostname, payload = {}) {
  const normalizedPayload = isObject(payload) ? payload : {};
  const params = {};

  const propertyId = normalizeText(normalizedPayload.property_id);
  const permitId = normalizeText(normalizedPayload.permit_id);
  const limit = Number(normalizedPayload.limit || 50);

  if (propertyId) {
    params.property_id = propertyId;
  }
  if (permitId) {
    params.permit_id = permitId;
  }
  params.limit = Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 50;

  const response = await axios.get(
    buildApiUrl(hostname, "/api/auth/custom-identifier/customer-identifiers/audit"),
    { params }
  );

  return response && response.data ? response.data : {};
}

export async function bulkUpdateCustomerIdentifiers(axios, hostname, payload = {}) {
  const normalizedPayload = isObject(payload) ? payload : {};
  return axios.post(
    buildApiUrl(hostname, "/api/auth/custom-identifier/customer-identifiers/bulk-update"),
    normalizedPayload
  );
}

export function normalizePermitCustomIdentifierOptionsPayload(payload) {
  const data = isObject(payload) ? payload : {};
  const keyDefinitions = {};
  const keyOptions = buildCustomIdentifierKeyOptions(data.custom_identifier_keys || []);

  keyOptions.forEach((option) => {
    keyDefinitions[option.value] = option.metadata || { key: option.value, display_name: option.text };
  });

  const valueOptions = {};
  const rawValueOptions = isObject(data.custom_identifier_values) ? data.custom_identifier_values : {};
  Object.keys(rawValueOptions).forEach((key) => {
    const normalizedKey = normalizeText(key);
    if (!normalizedKey) {
      return;
    }

    valueOptions[normalizedKey] = (Array.isArray(rawValueOptions[key]) ? rawValueOptions[key] : [])
      .filter((option) => !option || option.is_active !== false)
      .map((option) => {
        if (!isObject(option)) {
          const normalizedValue = normalizeText(option);
          return { value: normalizedValue, text: normalizedValue };
        }

        const value = normalizeText(option.value);
        const text = normalizeText(option.display_text) || value;
        return {
          value,
          text,
          metadata: option,
        };
      })
      .filter((option) => option.value !== "");
  });

  const relationshipDefinitions = isObject(data.custom_identifier_relationship_definitions)
    ? data.custom_identifier_relationship_definitions
    : {};
  const rawRelationships = isObject(data.custom_identifier_relationships)
    ? data.custom_identifier_relationships
    : {};
  const relationships = {};

  const relationshipKeys = [...new Set([...Object.keys(rawRelationships), ...Object.keys(relationshipDefinitions)])];
  relationshipKeys.forEach((relationshipKey) => {
    const definition = isObject(relationshipDefinitions[relationshipKey]) ? relationshipDefinitions[relationshipKey] : {};
    const directMap = isObject(rawRelationships[relationshipKey]) ? rawRelationships[relationshipKey] : {};
    const [rawSourceKey = "", rawTargetKey = ""] = String(relationshipKey || "").split("->");
    const sourceKey = normalizeText(definition.source_key) || normalizeText(rawSourceKey);
    const targetKey = normalizeText(definition.target_key) || normalizeText(rawTargetKey);
    if (!sourceKey || !targetKey) {
      return;
    }

    const normalizedDirectMap = {};
    Object.entries(directMap).forEach(([sourceValue, targetValue]) => {
      const normalizedSourceValue = normalizeText(sourceValue);
      const normalizedTargetValue = normalizeText(targetValue);
      if (!normalizedSourceValue || !normalizedTargetValue) {
        return;
      }
      normalizedDirectMap[normalizedSourceValue] = normalizedTargetValue;
    });

    relationships[`${sourceKey}->${targetKey}`] = {
      relationship_id: normalizeText(definition.relationship_id),
      source_key: sourceKey,
      target_key: targetKey,
      is_bidirectional: definition.is_bidirectional === true,
      permit_type_scope: Array.isArray(definition.permit_type_scope) ? definition.permit_type_scope : [],
      value_mappings: normalizedDirectMap,
    };

    if (definition.is_bidirectional === true) {
      const reverseKey = `${targetKey}->${sourceKey}`;
      if (!relationships[reverseKey]) {
        const reverseMap = {};
        Object.entries(normalizedDirectMap).forEach(([sourceValue, targetValue]) => {
          if (reverseMap[targetValue] === undefined) {
            reverseMap[targetValue] = sourceValue;
          }
        });

        relationships[reverseKey] = {
          relationship_id: normalizeText(definition.relationship_id),
          source_key: targetKey,
          target_key: sourceKey,
          is_bidirectional: true,
          permit_type_scope: Array.isArray(definition.permit_type_scope) ? definition.permit_type_scope : [],
          value_mappings: reverseMap,
        };
      }
    }
  });

  return {
    keyDefinitions,
    keyOptions,
    valueOptions,
    relationships,
  };
}